/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './site-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {createObjectTypeItemLink} from '../common/directives/adn-select/adn-select-item-link.util';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {normalizeSiteIabCategories} from './site-commons';
import {AdnTranslateService} from '../common/filters/adn-translate.service';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {BlocklistResource} from '../../components/api/resources/resource/blocklist.resource';
import {EarningsAccountResource} from '../../components/api/resources/resource/earnings-account.resource';
import {RulesetResource} from '../../components/api/resources/resource/ruleset.resource';
import {SiteGroupResource} from '../../components/api/resources/resource/site-group.resource';
import {SiteResource} from '../../components/api/resources/resource/site.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
@Component({
  standalone: false,
  selector: 'adn-site-edit-page',
  template
})
export class SiteEditPageComponent extends AdminResourceEditPageBase {
  readonly rulesetItemLink = createObjectTypeItemLink('Ruleset');
  readonly publisherItemLink = createObjectTypeItemLink('EarningsAccount');
  readonly blocklistItemLink = createObjectTypeItemLink('Blocklist');

  readonly scanCreativesPolicyOptions: Array<{ value: string | null; label: string; hint: string }> = [
    {value: null, label: 'No scanning', hint: 'No scanning is required for site'},
    {value: 'SAFE_DOMAIN_COOKIE_500', label: 'Schibsted Norway Policy', hint: 'Scan creatives and apply Schibsted Norway policy'},
    {value: 'SCHIBSTED_SWEDEN', label: 'Schibsted Sweden Policy', hint: 'Scan creatives and apply Schibsted Sweden policy'},
    {value: 'TV2_NO_COOKIE_500', label: 'TV2.no Policy', hint: 'Scan creatives and apply TV2.no policy'}
  ];

  showBlockLists = 'ACTIVE';
  showScannerBlockedCreatives = false;
  pureMarketplacePlatform = false;
  isPoliticalAdsPlatform = false;
  showSiteGroupsEditable = true;
  isAxPublisher = false;
  showMerchantSelection = false;
  showPaywalled = false;
  showScannerToggle = false;
  adminUserName = '';
  showOwnership = false;
  canUpdateSite = false;
  canEditAdvertisingAccess = false;
  canEditSiteTeams = false;
  canEditMarketplaceOwner = false;
  teamsWarning = false;
  politicalAdvertising: 'BLOCKED' | 'UNBLOCKED' = 'UNBLOCKED';

  availableMerchants: any[] = [];
  earningsAccounts: any[] | false = false;
  siteGroups: any[] | false = false;
  blocklists: any[] | false = false;
  rulesets: any[] | false = false;
  siteRulesets: any[] = [];
  targetingRulesets: any[] = [];
  sizeRulesets: any[] = [];
  layoutRulesets: any[] = [];
  allTeams: any[] | null = null;
  allMarketplacePublisherTeams: any[] = [];
  allNotAxPublisherTeams: any[] = [];

  showAllowedTech = false;
  expandCreativeSummary = false;
  expandCreativeSummaryAdmin = false;
  expandInitCreativeSummary = false;
  expandAllowedCreativeSummary = false;

  adnSummaryBlocked: any = {uniqueCount: 0, displayItems: [], displayCount: 0};
  adnSummaryInitiallyBlocked: any = {uniqueCount: 0, displayItems: [], displayCount: 0};
  adnSummaryAllowed: any = {uniqueCount: 0, displayItems: [], displayCount: 0};

  private creativeSummaryInitialized = false;
  private pendingAsyncLoads = 0;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(SiteGroupResource) private readonly SiteGroup: any,
    @Inject(SiteResource) private readonly Site: any,
    @Inject(BlocklistResource) private readonly Blocklist: any,
    @Inject(RulesetResource) private readonly Ruleset: any,
    @Inject(EarningsAccountResource) private readonly EarningsAccount: any,
    @Inject(TeamResource) private readonly Team: any,
    private readonly adnTranslate: AdnTranslateService,
    private readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);

    this.showScannerBlockedCreatives = LocalNetworkProfile.isExperimental();
    this.pureMarketplacePlatform = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isPoliticalAdsPlatform = LocalNetworkProfile.isPoliticalAdsPlatform();
    this.showSiteGroupsEditable = !this.pureMarketplacePlatform || LocalUserPermissions.hasAny('MANAGE_PUBLISHING');
    this.isAxPublisher = LocalUserPermissions.isAnyAxPublisher();

    const username = localUserProfile.get('username');
    if (_.includes(['adnuntius@adnuntius.com', 'broker1@adnuntius.com'], username)) {
      this.adminUserName = username;
      this.showPaywalled = true;
      if (this.showScannerBlockedCreatives) {
        this.showScannerToggle = true;
      }
    }

    const networkInfo = LocalNetworkInfo.obtain();
    this.availableMerchants = _.filter(networkInfo.getInfo('productFeedProductCounts') || [], (feed: any) => {
      return feed.productCount > 0;
    });
    this.showMerchantSelection = this.availableMerchants.length > 0;

    this.canUpdateSite = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || LocalUserPermissions.hasAny('MANAGE_PUBLISHING')
      || LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN')
      || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN');
    this.canEditAdvertisingAccess = LocalUserPermissions.hasNetwork('MANAGE_BUSINESS')
      || LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.canEditMarketplaceOwner = this.canEditAdvertisingAccess;
    this.canEditSiteTeams = this.canEditAdvertisingAccess
      || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN')
      || LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN');

    this.deferFormDirtyTracking();
    this.pendingAsyncLoads = this.pureMarketplacePlatform ? 4 : 3;
    this.loadEarningsAccounts();
    this.loadSiteGroups();
    this.loadBlocklists();
    if (this.pureMarketplacePlatform) {
      this.loadRulesets();
    }
    this.loadTeams();
  }

  get politicalAdvertisingBlocked(): boolean {
    return this.politicalAdvertising === 'BLOCKED';
  }

  set politicalAdvertisingBlocked(blocked: boolean) {
    this.politicalAdvertising = blocked ? 'BLOCKED' : 'UNBLOCKED';
    this.markExternalFormDirty();
  }

  get scanCreativesPolicyHint(): string {
    const policy = this.editingModel?.scanCreativesPolicy ?? null;
    const option = _.find(this.scanCreativesPolicyOptions, {value: policy});
    return option ? option.hint : this.scanCreativesPolicyOptions[0].hint;
  }

  compareScanCreativesPolicy(left: string | null | undefined, right: string | null | undefined): boolean {
    return (left ?? null) === (right ?? null);
  }

  protected afterInitEditingModel(): void {
    this.editingModel.renderOption = this.editingModel.renderOption || null;
    this.editingModel.filterTeamsType = this.editingModel.filterTeamsType || 'NONE';
    if (this.isNew || this.editingModel.scanCreativesPolicy === undefined) {
      this.editingModel.scanCreativesPolicy = null;
    }
    this.normalizeMerchantIds();
    normalizeSiteIabCategories(this.editingModel);
    this.runAfterInitAfterSave();
  }

  protected afterInitAfterSave(saveObj?: any): void {
    this.runAfterInitAfterSave(saveObj);
    if (!this.isNew) {
      this.creativeSummaryInitialized = false;
      this.initCreativeSummaryIfNeeded();
    }
  }

  protected onBeforeSubmit(): void {
    if (this.editingModel.politicalAdvertising !== this.politicalAdvertising) {
      if (this.politicalAdvertising === 'BLOCKED') {
        this.editingModel.politicalAdvertising = 'BLOCKED';
      } else {
        this.editingModel.politicalAdvertising = 'ALLOWED';
      }
    }
    if (!this.editingModel.country) {
      this.editingModel.country = null;
    }
    const submitMids = _.map(this.editingModel.retailMerchantIds || [], (mid: any) => {
      return _.isString(mid) ? parseInt(mid, 10) : mid;
    });
    this.editingModel.retailMerchantIds = _.uniq(_.filter(submitMids, _.isFinite));
    normalizeSiteIabCategories(this.editingModel);
    this.editingModel.siteGroups = _.map(this.editingModel.siteGroups || [], (sg: any) => {
      return {id: sg.id, name: sg.name};
    });
    if (this.editingModel.ownerTeam) {
      this.editingModel.teams = _.filter(this.editingModel.teams || [], (teamId: string) => {
        return !!_.find(this.allNotAxPublisherTeams, (team: any) => team.id === teamId);
      });
    }
  }

  countryChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  iabCategoriesChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  selectTeams(): void {
    if (!this.canEditSiteTeams) {
      return;
    }
    this.editingModel.teams = _.map(this.allNotAxPublisherTeams, 'id');
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    if (!this.canEditSiteTeams) {
      return;
    }
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  isTeamSelected(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    if (!this.canEditSiteTeams) {
      return;
    }
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.editingModel.teams || [])];
    const index = teams.indexOf(teamId);
    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }
    this.editingModel.teams = teams;
    this.markExternalFormDirty();
  }

  isMerchantSelected(merchantId: number): boolean {
    return (this.editingModel.retailMerchantIds || []).indexOf(merchantId) > -1;
  }

  toggleMerchant(merchantId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const mids = [...(this.editingModel.retailMerchantIds || [])];
    const index = mids.indexOf(merchantId);
    if (checked && index < 0) {
      mids.push(merchantId);
    } else if (!checked && index > -1) {
      mids.splice(index, 1);
    }
    this.editingModel.retailMerchantIds = mids;
    this.markExternalFormDirty();
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  trackByMerchantId(_index: number, merchant: any): number {
    return merchant.id;
  }

  translateViolation(violation: any): string {
    return this.adnTranslate.transform(violation.text, violation.code, violation.parameters);
  }

  private runAfterInitAfterSave(saveObj?: any): void {
    const data = saveObj || this.editingModel;
    if (saveObj) {
      this.teamsWarning = (_.get(this.editingModel, 'teams') || []).length !== (_.get(data, 'teams') || []).length;
    }
    this.editingModel.renderOption = this.editingModel.renderOption || null;
    this.editingModel.teams = _.map(_.get(data, 'teams') || this.editingModel.teams || [], (team: any) => {
      return _.isString(team) ? team : team.id;
    });
    this.normalizeMerchantIds(data);
    this.editingModel.scanCreativesPolicy = data.scanCreativesPolicy ? data.scanCreativesPolicy : null;

    this.politicalAdvertising = 'BLOCKED';
    if (!this.editingModel.politicalAdvertising || this.editingModel.politicalAdvertising !== 'BLOCKED') {
      this.politicalAdvertising = 'UNBLOCKED';
    }

    this.resolveEarningsAccountReference();
    this.resolveSiteGroupReferences();
    this.resolveBlocklistReference();
    this.resolveRulesetReferences();
    this.resolveOwnerTeamReference();
    if (!this.isNew) {
      this.initCreativeSummaryIfNeeded();
    }
  }

  private normalizeMerchantIds(data?: any): void {
    const source = data || this.editingModel;
    const savedMerchantIds = _.get(source, 'retailMerchantIds') || this.editingModel.retailMerchantIds || [];
    const coercedMids = _.map(savedMerchantIds, (mid: any) => {
      return _.isString(mid) ? parseInt(mid, 10) : mid;
    });
    this.editingModel.retailMerchantIds = _.filter(coercedMids, _.isFinite);
  }

  private initCreativeSummaryIfNeeded(): void {
    if (!this.showScannerBlockedCreatives || !this.editingModel?.scanCreativesPolicy || !this.editingModel?.id) {
      return;
    }
    if (this.creativeSummaryInitialized && this.adnSummaryBlocked?.moreParams?.id === this.editingModel.id) {
      return;
    }
    this.creativeSummaryInitialized = true;
    this.adnSummaryBlocked = this.createAdnCreativeSummaryList('BLOCKED');
    if (this.showScannerToggle) {
      this.adnSummaryInitiallyBlocked = this.createAdnCreativeSummaryList('INITIALLY_BLOCKED');
      this.adnSummaryAllowed = this.createAdnCreativeSummaryList('ALLOWED');
    }
  }

  private createAdnCreativeSummaryList(summaryType: string): any {
    const list: any = {uniqueCount: 0, displayItems: [], displayCount: 0};
    this.adnListHelper.setUpBasicList(list, this.Site, '', {
      listMethod: 'adnCreativeSummary',
      listGetMethod: 'adnCreativeSummary',
      paginate: false,
      moreParams: {
        id: this.editingModel.id,
        summaryType
      },
      afterFill: (items: any[]) => {
        deferMarkForCheck(this.cdr, () => {
          list.displayItems = (items || []).slice();
          list.displayCount = list.displayItems.length;
          list.uniqueCount = _.uniqBy(items, 'hash').length;
        });
      }
    });
    return list;
  }

  private loadEarningsAccounts(): void {
    this.EarningsAccount.query({minimal: true}, (page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.earningsAccounts = page.results || [];
        this.resolveEarningsAccountReference();
        this.onAsyncLoadComplete();
      });
    });
  }

  private loadSiteGroups(): void {
    this.SiteGroup.query({minimal: true}, (page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.siteGroups = page.results || [];
        this.resolveSiteGroupReferences();
        this.onAsyncLoadComplete();
      });
    });
  }

  private loadBlocklists(): void {
    this.Blocklist.query({minimal: true}, (page: any) => {
      // false → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.blocklists = page.results || [];
        this.resolveBlocklistReference();
        this.onAsyncLoadComplete();
      });
    });
  }

  private loadRulesets(): void {
    this.Ruleset.query({minimal: false}, (page: any) => {
      deferMarkForCheck(this.cdr, () => {
        const results = page.results || [];
        this.rulesets = results;
        this.siteRulesets = _.filter(results, (rs: any) => rs.type === 'INVENTORY');
        this.targetingRulesets = _.filter(results, (rs: any) => rs.type === 'TARGETING');
        this.sizeRulesets = _.filter(results, (rs: any) => rs.type === 'SIZE');
        this.layoutRulesets = _.filter(results, (rs: any) => rs.type === 'LAYOUT');
        this.resolveRulesetReferences();
        this.onAsyncLoadComplete();
      });
    });
  }

  private loadTeams(): void {
    this.Team.query({minimal: true}).$promise.then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.allTeams = page.results || [];
        this.allMarketplacePublisherTeams = _.filter(this.allTeams, (team: any) => team.type === 'AX_PUBLISHER');
        this.allNotAxPublisherTeams = _.filter(this.allTeams, (team: any) => {
          return team.type !== 'AX_PUBLISHER' && team.type !== 'CREATIVE_AGENCY';
        });
        this.showOwnership = this.allMarketplacePublisherTeams.length > 0;
        this.resolveOwnerTeamReference();
        this.onAsyncLoadComplete();
      });
    });
  }

  private onAsyncLoadComplete(): void {
    this.pendingAsyncLoads--;
    if (this.pendingAsyncLoads <= 0) {
      this.resumeFormDirtyTracking();
    }
  }

  private resolveEarningsAccountReference(): void {
    if (!_.isArray(this.earningsAccounts) || !this.editingModel?.earningsAccount?.id) {
      return;
    }
    const match = _.find(this.earningsAccounts, ['id', this.editingModel.earningsAccount.id]);
    if (match) {
      this.editingModel.earningsAccount = match;
    }
  }

  private resolveSiteGroupReferences(): void {
    if (!_.isArray(this.siteGroups) || !this.editingModel?.siteGroups?.length) {
      return;
    }
    this.editingModel.siteGroups = _.map(this.editingModel.siteGroups, (sg: any) => {
      const match = _.find(this.siteGroups as any[], ['id', sg.id]);
      return match || sg;
    });
  }

  private resolveBlocklistReference(): void {
    if (!_.isArray(this.blocklists) || !this.editingModel?.blocklist?.id) {
      return;
    }
    const match = _.find(this.blocklists, ['id', this.editingModel.blocklist.id]);
    if (match) {
      this.editingModel.blocklist = match;
    }
  }

  private resolveRulesetReferences(): void {
    if (!_.isArray(this.rulesets)) {
      return;
    }
    if (this.editingModel.siteRuleset?.id) {
      const siteRuleset = _.find(this.rulesets, ['id', this.editingModel.siteRuleset.id]);
      if (siteRuleset) {
        this.editingModel.siteRuleset = siteRuleset;
      }
    }
  }

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam || !this.allMarketplacePublisherTeams.length) {
      return;
    }
    const ownerTeamId = _.isString(this.editingModel.ownerTeam)
      ? this.editingModel.ownerTeam
      : this.editingModel.ownerTeam.id;
    const match = _.find(this.allMarketplacePublisherTeams, ['id', ownerTeamId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    }
  }
}
